import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { BookOpen, Tv, Film, Mic, LogOut, Plus, Eye, Users, Star, ThumbsUp, Meh, ThumbsDown, List, MoreVertical, Trash2, ChevronRight, Sparkles, Lock, ArrowRight, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { MobileNav } from '../components/MobileNav';
import { clearAuth } from '../lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SortaLogo = ({ size = "default" }) => {
  const sizeClasses = { small: "h-8", default: "h-10", large: "h-14" };
  return (
    <img 
      src="https://customer-assets.emergentagent.com/job_series-ranker-1/artifacts/barwtr5h_image.jpg"
      alt="Sorta"
      className={`${sizeClasses[size]} object-contain rounded-lg`}
    />
  );
};

// Book List Card Component
function BookListCard({ list, onClick, onDelete }) {
  const isJoint = list.list_type === 'joint';
  const bookCount = list.books?.length || 0;
  
  return (
    <div
      onClick={onClick}
      className="p-3 sm:p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-all cursor-pointer group"
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isJoint ? 'bg-purple-100' : 'bg-amber-100'}`}>
          {isJoint ? <Users className="w-5 h-5 text-purple-600" /> : <BookOpen className="w-5 h-5 text-amber-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-[#3c405b] truncate text-sm sm:text-base">{list.title}</h3>
          <p className="text-xs text-[#64748B]">
            {bookCount} {bookCount === 1 ? 'book' : 'books'}
            {isJoint && ` • ${1 + (list.shared_with?.length || 0)} members`}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => onDelete(e)} className="text-red-600 cursor-pointer">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete List
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-[#f87676] transition-colors" />
      </div>
    </div>
  );
}

// Read Book Card Component
function ReadBookCard({ book }) {
  const getGradeStyles = (grade) => {
    switch(grade) {
      case 'liked': return 'ring-green-400 bg-green-50';
      case 'disliked': return 'ring-red-400 bg-red-50';
      default: return 'ring-yellow-400 bg-yellow-50';
    }
  };
  
  const getGradeIcon = (grade) => {
    switch(grade) {
      case 'liked': return <ThumbsUp className="w-3 h-3 text-green-500" />;
      case 'disliked': return <ThumbsDown className="w-3 h-3 text-red-500" />;
      default: return <Meh className="w-3 h-3 text-yellow-500" />;
    }
  };

  return (
    <div className="text-center">
      <div className={`aspect-[2/3] rounded-lg overflow-hidden mb-2 ring-2 ${getGradeStyles(book.grade)}`}>
        {book.cover_image ? (
          <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-slate-100">
            <BookOpen className="w-6 h-6 text-slate-300" />
          </div>
        )}
      </div>
      <p className="text-xs font-medium text-[#3c405b] line-clamp-2">{book.title}</p>
      <div className="flex items-center justify-center gap-1 mt-1">
        {getGradeIcon(book.grade)}
        <span className="text-xs text-[#64748B]">#{book.rank_in_group}</span>
      </div>
    </div>
  );
}

export default function BooksPage({ user }) {
  const navigate = useNavigate();
  const [bookLists, setBookLists] = useState([]);
  const [readBooks, setReadBooks] = useState([]);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newListTitle, setNewListTitle] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [newListType, setNewListType] = useState('personal');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [listsRes, readRes, recsRes] = await Promise.all([
        axios.get(`${API}/book-lists`, { withCredentials: true }),
        axios.get(`${API}/read-books`, { withCredentials: true }),
        axios.get(`${API}/recommendations/books`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setBookLists(listsRes.data || []);
      setReadBooks(readRes.data || []);
      setRecommendations(recsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      clearAuth();
      navigate('/');
    } catch (error) {
      clearAuth();
      navigate('/');
    }
  };

  const handleCreateList = async () => {
    if (!newListTitle.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/book-lists?title=${encodeURIComponent(newListTitle)}&description=${encodeURIComponent(newListDescription)}&list_type=${newListType}`,
        {},
        { withCredentials: true }
      );
      setBookLists([response.data, ...bookLists]);
      setIsCreateDialogOpen(false);
      setNewListTitle('');
      setNewListDescription('');
      setNewListType('personal');
      toast.success(newListType === 'joint' ? 'Joint reading list created!' : 'Reading list created!');
    } catch (error) {
      toast.error('Failed to create list');
    }
  };

  const handleDeleteList = async (listId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this list?')) return;
    
    try {
      await axios.delete(`${API}/book-lists/${listId}`, { withCredentials: true });
      setBookLists(bookLists.filter(l => l.id !== listId));
      toast.success('List deleted');
    } catch (error) {
      toast.error('Failed to delete list');
    }
  };

  const totalInLists = bookLists.reduce((sum, list) => sum + (list.books?.length || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#3c405b] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#f87676] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#3c405b]">
      {/* Header - matching TV Shows style */}
      <header className="bg-[#f87676] sticky top-0 z-50 shadow-lg">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 sm:gap-8">
              <button onClick={() => navigate('/tv-shows')} className="flex items-center gap-2 group">
                <SortaLogo size="small" />
                <span className="font-outfit font-bold text-lg text-white hidden sm:inline group-hover:text-white/80 transition-colors">
                  Sorta
                </span>
              </button>
              
              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center gap-1">
                {/* Social */}
                <Button
                  variant="ghost"
                  onClick={() => navigate('/social')}
                  className="text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                  data-testid="nav-social"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Social
                </Button>
                {/* Messages */}
                <Button
                  variant="ghost"
                  onClick={() => navigate('/messages')}
                  className="text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                  data-testid="nav-messages"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Messages
                </Button>
                {(!user?.interests || user.interests.includes('tv')) && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/tv-shows')}
                    className="text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                  >
                    <Tv className="w-4 h-4 mr-2" />
                    TV Shows
                  </Button>
                )}
                {(!user?.interests || user.interests.includes('movies')) && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/films')}
                    className="text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                  >
                    <Film className="w-4 h-4 mr-2" />
                    Films
                  </Button>
                )}
                {(!user?.interests || user.interests.includes('podcasts')) && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/podcasts')}
                    className="text-white/80 hover:text-white hover:bg-white/10 rounded-full"
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    Podcasts
                  </Button>
                )}
                {(!user?.interests || user.interests.includes('books')) && (
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/books')}
                    className="bg-white/20 text-white hover:bg-white/30 rounded-full"
                    data-testid="nav-books"
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    Books
                  </Button>
                )}
              </nav>
            </div>
            
            {/* Desktop user menu */}
            <div className="hidden sm:flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="rounded-full p-0 w-10 h-10" data-testid="user-menu-btn">
                    <Avatar className="border-2 border-white/30">
                      {user?.avatar_url ? (
                        <AvatarImage src={user.avatar_url} alt={user?.display_name || user?.name} />
                      ) : (
                        <AvatarImage src={user?.picture} alt={user?.name} />
                      )}
                      <AvatarFallback className="bg-white text-[#f87676]">
                        {(user?.display_name || user?.name)?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-3 py-2 border-b">
                    <p className="font-medium text-sm truncate">{user?.display_name || user?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                    <Eye className="w-4 h-4 mr-2" />
                    View Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {/* Mobile avatar */}
            <div className="sm:hidden">
              <Avatar className="border-2 border-white/30 h-9 w-9">
                {user?.avatar_url ? (
                  <AvatarImage src={user.avatar_url} alt={user?.display_name || user?.name} />
                ) : user?.picture ? (
                  <AvatarImage src={user?.picture} alt={user?.name} />
                ) : (
                  <AvatarFallback className="bg-white text-[#f87676]">
                    {(user?.display_name || user?.name)?.charAt(0) || 'U'}
                  </AvatarFallback>
                )}
              </Avatar>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <MobileNav user={user} />

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Page Title */}
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/90 rounded-xl flex items-center justify-center shadow-lg">
              <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-[#f87676]" />
            </div>
            <div>
              <h1 className="font-outfit text-xl sm:text-2xl md:text-3xl font-bold text-white drop-shadow-lg">Books</h1>
              <p className="text-white/80 text-sm sm:text-base">{totalInLists} in lists &bull; {readBooks.length} read</p>
            </div>
          </div>
        </div>

        {/* Three-Tile Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {/* Lists Tile */}
          <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-outfit text-lg text-[#3c405b] flex items-center gap-2">
                  <List className="w-5 h-5" />
                  My Lists
                </CardTitle>
                <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      data-testid="create-list-btn"
                      size="sm"
                      className="bg-[#f87676] hover:bg-[#EF4444] text-white rounded-full"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      New List
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="create-list-dialog">
                    <DialogHeader>
                      <DialogTitle className="font-outfit text-[#3c405b]">Create Reading List</DialogTitle>
                      <DialogDescription className="text-[#64748B]">
                        Create a personal list or a joint list to rank with friends.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label className="text-sm font-medium text-[#3c405b] mb-2 block">List Type</Label>
                        <RadioGroup value={newListType} onValueChange={setNewListType} className="flex gap-4">
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="personal" id="personal-book" />
                            <Label htmlFor="personal-book" className="flex items-center gap-2 cursor-pointer">
                              <List className="w-4 h-4" />
                              Personal
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="joint" id="joint-book" />
                            <Label htmlFor="joint-book" className="flex items-center gap-2 cursor-pointer">
                              <Users className="w-4 h-4" />
                              Joint List
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-[#3c405b] mb-2 block">Title</Label>
                        <Input
                          data-testid="list-title-input"
                          placeholder="e.g., Sci-Fi Must Reads"
                          value={newListTitle}
                          onChange={(e) => setNewListTitle(e.target.value)}
                          className="border-slate-200"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-[#3c405b] mb-2 block">Description (optional)</Label>
                        <Textarea
                          data-testid="list-description-input"
                          placeholder="What's this list about?"
                          value={newListDescription}
                          onChange={(e) => setNewListDescription(e.target.value)}
                          className="border-slate-200"
                          rows={2}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="ghost" onClick={() => setIsCreateDialogOpen(false)} className="rounded-full">
                        Cancel
                      </Button>
                      <Button
                        data-testid="create-list-submit"
                        onClick={handleCreateList}
                        className="bg-[#f87676] hover:bg-[#EF4444] text-white rounded-full"
                      >
                        Create List
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {bookLists.length === 0 ? (
                <div className="text-center py-8">
                  <List className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-[#64748B] mb-4">No reading lists yet</p>
                  <Button
                    onClick={() => setIsCreateDialogOpen(true)}
                    variant="outline"
                    className="rounded-full border-[#f87676] text-[#f87676] hover:bg-[#f87676] hover:text-white"
                  >
                    Create your first list
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 max-h-[350px] overflow-y-auto">
                  {bookLists.map((list) => (
                    <BookListCard
                      key={list.id}
                      list={list}
                      onClick={() => navigate(`/book-list/${list.id}`)}
                      onDelete={(e) => handleDeleteList(list.id, e)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Read Tile */}
          <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-outfit text-lg text-[#3c405b] flex items-center gap-2">
                  <ThumbsUp className="w-5 h-5" />
                  Read
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/books-read')}
                  className="text-[#f87676] hover:text-[#EF4444]"
                >
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {readBooks.length === 0 ? (
                <div className="text-center py-8">
                  <ThumbsUp className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-[#64748B]">No books read yet</p>
                  <p className="text-sm text-[#94A3B8] mt-1">
                    Mark books as read from your lists
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3 max-h-[350px] overflow-y-auto">
                  {readBooks.slice(0, 9).map((book) => (
                    <ReadBookCard key={book.id} book={book} />
                  ))}
                </div>
              )}
              {readBooks.length > 9 && (
                <p className="text-center text-[#64748B] text-sm mt-4">
                  +{readBooks.length - 9} more read
                </p>
              )}
            </CardContent>
          </Card>

          {/* Discover/Recommendations Tile */}
          <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-outfit text-lg text-[#3c405b] flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Discover
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/books-discover')}
                  className="text-[#f87676] hover:text-[#EF4444]"
                  data-testid="discover-books-btn"
                >
                  Explore <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!recommendations?.unlocked ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-[#f87676]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-[#f87676]" />
                  </div>
                  <p className="text-[#3c405b] font-medium mb-2">Smart Recommendations Locked</p>
                  <p className="text-sm text-[#64748B] mb-4">
                    Read {10 - (recommendations?.read_count || 0)} more books to unlock personalized recommendations
                  </p>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-4">
                    <div 
                      className="h-full bg-[#f87676] rounded-full transition-all"
                      style={{ width: `${Math.min(((recommendations?.read_count || 0) / 10) * 100, 100)}%` }}
                    />
                  </div>
                  <Button
                    onClick={() => navigate('/books-discover')}
                    variant="outline"
                    className="rounded-full border-[#f87676] text-[#f87676] hover:bg-[#f87676] hover:text-white"
                  >
                    Browse All Books
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-[#64748B] mb-1">
                    Based on books you liked
                  </p>
                  {recommendations.top_genres?.length > 0 && (
                    <p className="text-xs text-[#94A3B8] mb-3">
                      Your top genres: {recommendations.top_genres.slice(0, 3).join(', ')}
                    </p>
                  )}
                  <div className="grid grid-cols-3 gap-3 max-h-[350px] overflow-y-auto">
                    {recommendations.recommendations?.slice(0, 6).map((book) => (
                      <div 
                        key={book.book_id}
                        className="cursor-pointer group text-center"
                        onClick={() => navigate(`/books-discover?search=${encodeURIComponent(book.title)}`)}
                      >
                        <div className="aspect-[2/3] rounded-lg overflow-hidden mb-1">
                          {book.cover_image ? (
                            <img 
                              src={book.cover_image}
                              alt={book.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-slate-100">
                              <BookOpen className="w-6 h-6 text-slate-300" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs font-medium text-[#3c405b] line-clamp-2 group-hover:text-[#f87676] transition-colors">
                          {book.title}
                        </p>
                      </div>
                    ))}
                  </div>
                  <Button
                    onClick={() => navigate('/books-recommendations')}
                    className="w-full mt-4 bg-[#f87676] hover:bg-[#EF4444] text-white rounded-full"
                    data-testid="see-all-recommendations-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    See All Recommendations
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
